import React, { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import { ColumnDef } from "@tanstack/react-table";
import ChartCard from "../../components/common/ChartCard";
import Loader from "../../components/common/Loader";
import Table from "../../components/common/Table";
import { useDashboard } from "../../hooks/useDashboard";
import { useAuth } from "../../hooks/useAuth";
import { ChartDataPoint, TenantInfo } from "../../schemas/dashboardSchema";

function getDistinctColor(index: number): string {
  // Golden-angle hue stepping gives stable, well-separated colors.
  // Using constant saturation/lightness keeps all slices equally readable.
  const hue = Math.round((index * 137.508 + 20) % 360);
  return `hsl(${hue} 72% 48%)`;
}

function buildDistinctColors(count: number): string[] {
  return Array.from({ length: count }, (_, index) => getDistinctColor(index));
}

// Recharts spends `paddingAngle` degrees of the circle on every slice, so a
// fixed gap silently eats the whole 360° once the slice count grows (130
// regions x 3° = 390°, which renders nothing at all). Cap the combined gap
// instead: small datasets keep the full 3°, large ones shrink to fit.
const MAX_TOTAL_PADDING_ANGLE = 24;
function getSlicePadding(count: number): number {
  if (count < 2) {
    return 0;
  }
  return Math.min(3, MAX_TOTAL_PADDING_ANGLE / count);
}

const PieLegend: React.FC<{
  data: ChartDataPoint[];
  colors: string[];
  className?: string;
}> = ({ data, colors, className }) => {
  if (data.length === 0) {
    return null;
  }

  return (
    <div
      className={`mt-3 flex max-h-24 flex-wrap items-start justify-center gap-x-3 gap-y-1 overflow-y-auto px-2 ${className || ""}`}
    >
      {data.map((item, index) => (
        <div
          key={`legend-${item.id || item.name}-${index}`}
          className="flex items-center gap-1"
        >
          <span
            className="inline-block h-3 w-3 rounded-full"
            style={{ backgroundColor: colors[index % colors.length] }}
          />
          <span className="text-xs text-gray-600">
            {item.name} ({item.value})
          </span>
        </div>
      ))}
    </div>
  );
};

// ─── Super Admin Dashboard ──────────────────────────────────────────────────

const STAT_CARDS = [
  {
    key: "totalTenants" as const,
    label: "Total Tenants",
    color: "bg-primary-50 text-primary-700 border-primary-200",
    iconBg: "bg-primary-100",
    iconColor: "text-primary-600",
  },
  {
    key: "premiumTenants" as const,
    label: "Premium Tenants",
    color: "bg-success-50 text-success-700 border-success-200",
    iconBg: "bg-success-100",
    iconColor: "text-success-600",
  },
  {
    key: "nonPremiumTenants" as const,
    label: "Non-Premium Tenants",
    color: "bg-warning-50 text-warning-700 border-warning-200",
    iconBg: "bg-warning-100",
    iconColor: "text-warning-600",
  },
  {
    key: "deletedTenants" as const,
    label: "Deleted Tenants",
    color: "bg-danger-50 text-danger-700 border-danger-200",
    iconBg: "bg-danger-100",
    iconColor: "text-danger-600",
  },
];

const tenantColumns: ColumnDef<TenantInfo>[] = [
  {
    accessorKey: "companyName",
    header: "Company Name",
    cell: ({ row }) => (
      <span className="font-medium text-gray-900">
        {row.original.companyName}
      </span>
    ),
  },
  {
    accessorKey: "address",
    header: "Address",
    cell: ({ row }) => (
      <span className="text-gray-600">{row.original.address || "—"}</span>
    ),
  },
  {
    accessorKey: "isPremium",
    header: "Plan",
    cell: ({ row }) =>
      row.original.isPremium ? (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-700">
          Premium
        </span>
      ) : (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          Standard
        </span>
      ),
  },
  {
    accessorKey: "dbName",
    header: "Database",
    cell: ({ row }) => (
      <span className="text-gray-500 font-mono text-sm">
        {row.original.dbName || "—"}
      </span>
    ),
  },
  {
    accessorKey: "createdAt",
    header: "Created At",
    cell: ({ row }) => (
      <span className="text-gray-600">
        {new Date(Number(row.original.createdAt)).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })}
      </span>
    ),
  },
  {
    accessorKey: "isDeleted",
    header: "Status",
    cell: ({ row }) =>
      row.original.isDeleted ? (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-danger-100 text-danger-700">
          Deleted
        </span>
      ) : (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-700">
          Active
        </span>
      ),
  },
];

const SuperAdminDashboard: React.FC<{
  data: NonNullable<ReturnType<typeof useDashboard>["superAdminDashboardData"]>;
}> = React.memo(({ data }) => {
  return (
    <div className="min-h-screen p-6 space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        {STAT_CARDS.map((card) => (
          <div
            key={card.key}
            className={`rounded-2xl border p-5 shadow-sm transition hover:shadow-md ${card.color}`}
          >
            <p className="text-sm font-medium opacity-75 mb-1">{card.label}</p>
            <p className="text-3xl font-bold">{data[card.key]}</p>
          </div>
        ))}
      </div>

      {/* Growth Trend Chart */}
      <ChartCard title="Tenant Growth Trend">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data.tenantGrowthTrend}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Line
              type="monotone"
              dataKey="count"
              stroke="#1E90FF"
              strokeWidth={2}
              dot={{ r: 4, fill: "#1E90FF" }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Tenant List Table */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <h2 className="font-semibold text-xl mb-4 text-secondary-800">
          All Tenants
        </h2>
        <Table<TenantInfo>
          data={data.tenants}
          columns={tenantColumns}
          searchable
          pagination
        />
      </div>
    </div>
  );
});

SuperAdminDashboard.displayName = "SuperAdminDashboard";

// ─── Normal Dashboard ───────────────────────────────────────────────────────

const NormalDashboard: React.FC<{
  data: NonNullable<ReturnType<typeof useDashboard>["dashboardData"]>;
}> = React.memo(({ data }) => {
  const [selectedCityId, setSelectedCityId] = useState<string | null>(null);
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null);
  const {
    barData = [],
    offSinceData = [],
    updateData = [],
    pieDataCity = [],
    pieDataArea = [],
    cityBreakdown = [],
  } = data;

  const selectedCity = useMemo(
    () => cityBreakdown.find((city) => city.cityId === selectedCityId) || null,
    [cityBreakdown, selectedCityId],
  );

  const cityPieData = useMemo(() => {
    if (cityBreakdown.length > 0) {
      return cityBreakdown.map((city) => ({
        id: city.cityId,
        name: city.cityName,
        value: city.totalScreens,
      }));
    }
    return pieDataCity;
  }, [cityBreakdown, pieDataCity]);

  const cityOnOffData = useMemo(() => {
    if (!selectedCity) {
      return [];
    }
    return [
      { name: "Screens On", value: selectedCity.onScreens },
      { name: "Screens Off", value: selectedCity.offScreens },
    ];
  }, [selectedCity]);

  const regionBreakdownData = useMemo(() => {
    if (selectedCity) {
      return selectedCity.regions.map((region) => ({
        id: region.regionId,
        name: region.regionName,
        totalScreens: region.totalScreens,
        onScreens: region.onScreens,
        offScreens: region.offScreens,
      }));
    }

    if (cityBreakdown.length > 0) {
      const regionMap = new Map<
        string,
        {
          id: string;
          name: string;
          totalScreens: number;
          onScreens: number;
          offScreens: number;
        }
      >();
      cityBreakdown.forEach((city) => {
        city.regions.forEach((region) => {
          const existing = regionMap.get(region.regionId);
          regionMap.set(region.regionId, {
            id: region.regionId,
            name: region.regionName,
            totalScreens: (existing?.totalScreens || 0) + region.totalScreens,
            onScreens: (existing?.onScreens || 0) + region.onScreens,
            offScreens: (existing?.offScreens || 0) + region.offScreens,
          });
        });
      });
      return Array.from(regionMap.values()).sort(
        (a, b) => b.totalScreens - a.totalScreens,
      );
    }

    return pieDataArea.map((region) => ({
      id: region.id || region.name,
      name: region.name,
      totalScreens: region.value,
      onScreens: 0,
      offScreens: 0,
    }));
  }, [selectedCity, cityBreakdown, pieDataArea]);

  const regionPieData = useMemo(
    () =>
      regionBreakdownData.map((region) => ({
        id: region.id,
        name: region.name,
        value: region.totalScreens,
      })),
    [regionBreakdownData],
  );

  const selectedRegion = useMemo(
    () =>
      regionBreakdownData.find((region) => region.id === selectedRegionId) ||
      null,
    [regionBreakdownData, selectedRegionId],
  );

  const regionOnOffData = useMemo(() => {
    if (!selectedRegion) {
      return [];
    }
    return [
      { name: "Screens On", value: selectedRegion.onScreens },
      { name: "Screens Off", value: selectedRegion.offScreens },
    ];
  }, [selectedRegion]);

  const citySliceColors = useMemo(
    () => buildDistinctColors(cityPieData.length),
    [cityPieData.length],
  );

  const regionSliceColors = useMemo(
    () => buildDistinctColors(regionPieData.length),
    [regionPieData.length],
  );

  const totalScreens = useMemo(() => {
    return barData.reduce(
      (sum: number, item: ChartDataPoint) => sum + item.value,
      0,
    );
  }, [barData]);

  const screensOn = useMemo(() => {
    const onItem = barData.find((item: ChartDataPoint) => item.name === "On");
    return onItem?.value || 0;
  }, [barData]);

  const screensOff = useMemo(() => {
    const offItem = barData.find((item: ChartDataPoint) => item.name === "Off");
    return offItem?.value || 0;
  }, [barData]);

  const maxBarValue = useMemo(() => {
    const allValues = [
      ...barData.map((d: ChartDataPoint) => d.value),
      ...offSinceData.map((d: ChartDataPoint) => d.value),
      ...updateData.map((d: ChartDataPoint) => d.value),
    ];
    return Math.max(...allValues, 1);
  }, [barData, offSinceData, updateData]);

  const handleCitySliceClick = (entry: ChartDataPoint) => {
    const cityId = entry?.id;
    if (!cityId) {
      return;
    }
    setSelectedRegionId(null);
    setSelectedCityId((current) => (current === cityId ? null : cityId));
  };

  const handleRegionSliceClick = (entry: ChartDataPoint) => {
    const regionId = entry?.id;
    if (!regionId) {
      return;
    }
    setSelectedRegionId((current) => (current === regionId ? null : regionId));
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 min-h-screen p-6">
      {/* Active Screens */}
      <ChartCard title="Active Screens">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-2xl font-bold text-primary-default mb-2">
              Total Screens: {totalScreens}
            </div>
            <div className="text-lg text-success-default">
              Screens On: {screensOn}
            </div>
            <div className="text-lg text-danger-default">
              Screens Off: {screensOff}
            </div>
          </div>
          <BarChart width={180} height={120} data={barData} barSize={40}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis domain={[0, maxBarValue]} />
            <Tooltip />
            <Bar dataKey="value" fill="#1E90FF" />
          </BarChart>
        </div>
      </ChartCard>

      {/* Screens Off Duration */}
      <ChartCard title="Some screens are off since a while">
        <BarChart width={280} height={150} data={offSinceData} barSize={40}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis allowDecimals={false} />
          <Tooltip />
          <Bar dataKey="value" fill="#1E90FF" />
        </BarChart>
      </ChartCard>

      {/* Content Update Status */}
      <ChartCard title="Screens needing content update">
        <BarChart width={280} height={150} data={updateData} barSize={40}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis allowDecimals={false} />
          <Tooltip />
          <Bar dataKey="value" fill="#F97316" />
        </BarChart>
      </ChartCard>

      {/* Pie Chart - City */}
      <ChartCard
        title={
          selectedCity
            ? `On/Off in ${selectedCity.cityName}`
            : "Screens by City"
        }
      >
        {selectedCity ? (
          <div className="flex flex-col items-center">
            <p className="text-xs text-gray-500 mb-2">
              Click chart or reset to view all cities
            </p>
            <PieChart width={240} height={260}>
              <Pie
                data={cityOnOffData}
                cx={120}
                cy={95}
                innerRadius={52}
                outerRadius={72}
                paddingAngle={3}
                dataKey="value"
                onClick={() => {
                  setSelectedRegionId(null);
                  setSelectedCityId(null);
                }}
              >
                {cityOnOffData.map((entry: ChartDataPoint, index: number) => (
                  <Cell
                    key={`cell-city-status-${index}`}
                    fill={entry.name === "Screens On" ? "#10B981" : "#EF4444"}
                  />
                ))}
              </Pie>
              <Tooltip formatter={(value, name) => [value, name]} />
            </PieChart>
            <PieLegend data={cityOnOffData} colors={["#10B981", "#EF4444"]} />
            <button
              className="text-xs text-primary-default underline"
              onClick={() => {
                setSelectedRegionId(null);
                setSelectedCityId(null);
              }}
            >
              Reset city selection
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <PieChart width={240} height={220}>
              <Pie
                data={cityPieData}
                cx={120}
                cy={96}
                innerRadius={58}
                outerRadius={82}
                paddingAngle={getSlicePadding(cityPieData.length)}
                dataKey="value"
                onClick={(entry) =>
                  handleCitySliceClick(entry as ChartDataPoint)
                }
              >
                {cityPieData.map((entry: ChartDataPoint, index: number) => (
                  <Cell
                    key={`cell-city-${index}`}
                    fill={citySliceColors[index]}
                  />
                ))}
              </Pie>
              <Tooltip formatter={(value, name) => [value, name]} />
            </PieChart>
            <PieLegend
              data={cityPieData}
              colors={citySliceColors}
              className="max-w-[320px]"
            />
          </div>
        )}
      </ChartCard>

      {/* Pie Chart - Area */}
      <ChartCard
        className="xl:col-span-2"
        title={
          selectedRegion
            ? `On/Off in ${selectedRegion.name}`
            : selectedCity
              ? `Regions in ${selectedCity.cityName} (Total: ${selectedCity.totalScreens})`
              : "Screens by Area"
        }
      >
        <div className="flex w-full flex-col items-center">
          {selectedRegion ? (
            <>
              <p className="text-xs text-gray-500 mb-2">
                Click chart or reset to view all regions
              </p>
              <PieChart width={240} height={260}>
                <Pie
                  data={regionOnOffData}
                  cx={120}
                  cy={95}
                  innerRadius={52}
                  outerRadius={72}
                  paddingAngle={3}
                  dataKey="value"
                  onClick={() => setSelectedRegionId(null)}
                >
                  {regionOnOffData.map(
                    (entry: ChartDataPoint, index: number) => (
                      <Cell
                        key={`cell-region-status-${index}`}
                        fill={
                          entry.name === "Screens On" ? "#10B981" : "#EF4444"
                        }
                      />
                    ),
                  )}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
              </PieChart>
              <PieLegend
                data={regionOnOffData}
                colors={["#10B981", "#EF4444"]}
              />
              <button
                className="text-xs text-primary-default underline"
                onClick={() => setSelectedRegionId(null)}
              >
                Reset region selection
              </button>
            </>
          ) : (
            <>
              <PieChart width={240} height={220}>
                <Pie
                  data={regionPieData}
                  cx={120}
                  cy={96}
                  innerRadius={58}
                  outerRadius={82}
                  paddingAngle={getSlicePadding(regionPieData.length)}
                  dataKey="value"
                  onClick={(entry) =>
                    handleRegionSliceClick(entry as ChartDataPoint)
                  }
                >
                  {regionPieData.map((entry: ChartDataPoint, index: number) => (
                    <Cell
                      key={`cell-area-${index}`}
                      fill={regionSliceColors[index]}
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
              </PieChart>
              <PieLegend
                data={regionPieData}
                colors={regionSliceColors}
                className="w-full"
              />
            </>
          )}
        </div>
      </ChartCard>
    </div>
  );
});

NormalDashboard.displayName = "NormalDashboard";

// ─── Main Dashboard ─────────────────────────────────────────────────────────

const Dashboard: React.FC = () => {
  const { profile } = useAuth();
  const { dashboardData, superAdminDashboardData, isLoading, isSuperAdmin } =
    useDashboard(profile?.role);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader size="large" />
      </div>
    );
  }

  if (isSuperAdmin) {
    if (!superAdminDashboardData) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <p className="text-gray-500">No dashboard data available</p>
        </div>
      );
    }
    return <SuperAdminDashboard data={superAdminDashboardData} />;
  }

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">No dashboard data available</p>
      </div>
    );
  }

  return <NormalDashboard data={dashboardData} />;
};

export default Dashboard;
